import { queryOptions, useQuery } from '@tanstack/react-query';
import { getValidated, AppError } from '@shared/api';
import { groupKeys } from './keys';
import { GroupIndexResponseSchema, GroupDtoSchema, type GroupIndexItem } from './schemas';
import { toGroup, toGroupListItem } from './mappers';

/** The backend pages this list 20 groups at a time (same convention as the ledger feed). */
const GROUPS_PAGE_SIZE = 20;
/** Defensive ceiling — 100 pages = 2,000 groups, far beyond any real account. */
const GROUPS_MAX_PAGES = 100;

/**
 * All of the user's groups (any status). `state=all` returns every group with its
 * `is_archived` / `is_closed` flags so the rail can filter the Active / Archived / Closed / All
 * tabs client-side. The backend answers 404 when the user has no groups — treated as empty.
 *
 * The endpoint is PAGED (20 groups per `page`, 1-based — the same convention as
 * `GET /api/v1/expense/{groupId}/new`), so every page is fetched and merged: a 21st group used
 * to be invisible on the web. Pagination stops on a short page, an empty page, a 404 past the
 * first page, or a page that adds no new ids (a backend that ignored `page` would otherwise
 * repeat page 1 forever). The first request carries no `page` at all — identical to what was
 * sent before — so an account within one page sees exactly the previous behaviour.
 */
export const groupsQueryOptions = queryOptions({
  queryKey: groupKeys.list(),
  queryFn: async () => {
    const seen = new Set<string>();
    const groups: GroupIndexItem[] = [];
    for (let page = 1; page <= GROUPS_MAX_PAGES; page += 1) {
      let pageGroups: GroupIndexItem[];
      try {
        const res = await getValidated('api/v1/group', GroupIndexResponseSchema, {
          state: 'all',
          ...(page > 1 ? { page } : {}),
        });
        pageGroups = res.data.groups;
      } catch (error) {
        if (error instanceof AppError && error.status === 404) break;
        throw error;
      }
      let added = 0;
      for (const group of pageGroups) {
        if (seen.has(group._id)) continue;
        seen.add(group._id);
        groups.push(group);
        added += 1;
      }
      if (pageGroups.length < GROUPS_PAGE_SIZE || added === 0) break;
    }
    return groups;
  },
  // Mapping in `select` keeps DTOs out of components — they only ever see domain types.
  select: (groups) => groups.map(toGroupListItem),
});

export function useGroups() {
  return useQuery(groupsQueryOptions);
}

export function groupQueryOptions(id: string) {
  return queryOptions({
    queryKey: groupKeys.detail(id),
    queryFn: () => getValidated(`api/groups/${id}`, GroupDtoSchema),
    select: toGroup,
  });
}

export function useGroup(id: string) {
  return useQuery(groupQueryOptions(id));
}
